import { useQueries } from '@tanstack/react-query';

import { useConnection } from 'services/repositories/connections/connections';
import { useInfinitePartners, usePartner } from 'services/repositories/partners/partners';
import { useRelationship } from 'services/repositories/relationships/relationships';
import { formatBasedOnSortArray } from 'support/helpers/arrays/arrays';
import { DOCUMENT_TYPE_ORDER } from 'support/helpers/const/const';
import { ConnectionPartner, ConnectionRelationship, DocumentType, PartnerDTO } from 'support/types';
import { convertToPartnerV2ToRequiredPartnerDTO } from 'pages/TradePartners/NewTradeRequest/NewTradeRequest';

type PartnerForTradeRequest = Pick<PartnerDTO, 'id' | 'documentTypes' | 'logoUrl' | 'name'>;

type ExistingData = {
  partnerForTradeRequest?: PartnerForTradeRequest;
};

type UsePartnerToConnectToResult = {
  partner: PartnerForTradeRequest | undefined;
  partnerWithRelationships: ConnectionPartner | undefined;
  relationships: Array<ConnectionRelationship>;
  isLoading: boolean;
  isConnected: boolean;
  showNoConnection: boolean;
  isWaitingForPartnerOnboarding: boolean;
};

export const usePartnerToConnectTo = (
  partnerId: string | undefined,
  existingData?: ExistingData,
): UsePartnerToConnectToResult => {
  const { partnerForTradeRequest: existingPartnerForTradeRequest } = existingData ?? {};

  // 1. The connection response is the only source of relationships. A 404 (not connected) resolves the query to
  //    null, which is all this hook needs to derive the not-connected state.
  // isInitialLoading stays false while the query is disabled (no partnerId).
  const { data: connectionData, isInitialLoading: connectionLoading } = useConnection({ variables: { partnerId } });

  const partnerFromConnection: ConnectionPartner | undefined = connectionData?.data;

  // 2. Derive connection status from the show response
  const isConnected = Boolean(partnerFromConnection);

  // 3. Only fetch partner data if NOT provided via existingData
  const needsPartnerFetch = shouldFetchPartner(existingPartnerForTradeRequest, partnerId);

  const { data: basicPartner, isLoading: basicLoading } = usePartner({
    variables: { id: partnerId },
    enabled: needsPartnerFetch,
  });

  // 4. Only fetch partners search if: no existingPartnerData AND not connected AND have name
  const basicPartnerName = basicPartner?.name;
  const needsPartnersSearch = shouldSearchPartners(
    needsPartnerFetch,
    connectionLoading,
    partnerFromConnection,
    basicPartnerName,
  );

  const { data: partnersSearchData, isLoading: partnersSearchLoading } = useInfinitePartners({
    variables: {
      query: {
        filterValues: basicPartnerName ? [{ key: 'name', value: basicPartnerName }] : undefined,
        perPage: '10',
      },
    },
    enabled: needsPartnersSearch,
  });

  const partnerSearchPages = partnersSearchData?.pages ?? [];
  const partnerFromSearch = partnerSearchPages.flatMap((page) => page.items).find((p) => p.id === partnerId);

  // 5. Compute relationships from connected partner data
  const { receiver_relationships: receiverRelationships = [], sender_relationships: senderRelationships = [] } =
    partnerFromConnection ?? {};
  const relationships: Array<ConnectionRelationship> = [...receiverRelationships, ...senderRelationships].sort(
    formatBasedOnSortArray(DOCUMENT_TYPE_ORDER, (array, value) => array.indexOf(value.message_type as DocumentType)),
  );

  // 6. Prefetch relationship details
  useQueries({
    queries: relationships.map((relationship) => useRelationship.getFetchOptions({ relationshipId: relationship.id })),
  });

  // 7. Derive partner from priority: existingData > connection > search
  const partner = selectPartner(existingPartnerForTradeRequest, partnerFromConnection, partnerFromSearch);

  // 8. Loading state
  const isLoading =
    connectionLoading || (needsPartnerFetch && (basicLoading || (needsPartnersSearch && partnersSearchLoading)));

  // 9. Show no connection state
  const showNoConnection = shouldShowNoConnection(isConnected, isLoading, partner);

  // 10. Waiting for partner onboarding
  const isWaitingForPartnerOnboarding = relationships.some(
    (relationship) => relationship.status === 'PARTNER_ONBOARDING_PENDING',
  );

  return {
    partner,
    partnerWithRelationships: partnerFromConnection,
    relationships,
    isLoading,
    isConnected,
    showNoConnection,
    isWaitingForPartnerOnboarding,
  };
};

const shouldFetchPartner = (
  existingPartner: PartnerForTradeRequest | undefined,
  partnerId: string | undefined,
): boolean => !existingPartner && Boolean(partnerId);

const shouldSearchPartners = (
  needsPartnerFetch: boolean,
  connectionLoading: boolean,
  partnerFromConnection: ConnectionPartner | undefined,
  basicPartnerName: string | undefined,
): boolean => needsPartnerFetch && !connectionLoading && !partnerFromConnection && Boolean(basicPartnerName);

const selectPartner = (
  existingPartner: PartnerForTradeRequest | undefined,
  partnerFromConnection: ConnectionPartner | undefined,
  partnerFromSearch: PartnerForTradeRequest | undefined,
): PartnerForTradeRequest | undefined =>
  existingPartner ||
  (partnerFromConnection ? convertToPartnerV2ToRequiredPartnerDTO(partnerFromConnection) : undefined) ||
  partnerFromSearch;

const shouldShowNoConnection = (
  isConnected: boolean,
  isLoading: boolean,
  partner: PartnerForTradeRequest | undefined,
): boolean => !isConnected && !isLoading && Boolean(partner);
